/**
 * `deriveWorkout` — the orchestrator. Joins lap events, HR samples, stroke
 * counts, and metadata from a SwimFirehoseResponse into a single shape every
 * prototype consumes. Each computation step lives in its own module under
 * `web/src/lib/`; this file's job is to call them in dependency order and
 * package the result.
 *
 * Public surface: `deriveWorkout(dump) -> DerivedWorkout` plus the type
 * re-exports below. Prototypes that imported types from this module before
 * the split keep working without changes.
 */
import type { SwimFirehoseResponse, FirehoseTypeBucket, LapLabel } from "@contract/types";
import { buildLaps, buildOpenWaterSplits, detectPoolLength, isOpenWaterFirehose, type Lap } from "./laps";
import { applyStrokeOverrides } from "./strokeOverrides";
import { stampEffectiveExcluded } from "./exclusions";
import { buildHrSeries } from "./hrSeries";
import { buildSetGroups, type SetGroup } from "./setGroups";
import { buildRestIntervals, detectRestIntervals, type RestInterval } from "./restIntervals";
import { buildPhases, type WorkoutPhase } from "./phases";
import { computeWorkoutAggregates, type StrokeBreakdownEntry } from "./workoutAggregates";
import { computeSwolfStats, type SwolfStats } from "./swolf";
import { findStrokeSuspects, confirmedLapIndices, type Suspect } from "./strokeSuspects";
import { buildCorrectionsViewModel, emptyCorrections, type CorrectionsViewModel } from "./corrections";

// Re-exports — preserve the public API for prototype imports across the split.
export type { Lap, SetGroup, RestInterval, WorkoutPhase, SwolfStats, StrokeBreakdownEntry };
export { detectRestIntervals };

export interface DerivedWorkout {
  laps: Lap[];
  poolLengthMeters: number | null;
  totalDistanceMeters: number;
  totalDurationSeconds: number;
  avgHr: number | null;
  maxHr: number | null;
  peakHrTs: number | null;
  peakLapIndex: number | null;     // lap with highest avg HR
  fastestLapIndex: number | null;  // lap with shortest duration
  strokeBreakdown: StrokeBreakdownEntry[];
  hrSeries: { ts: number; bpm: number }[]; // for line charts
  /** Watch-segment sets with tiny-gap merge — the app-wide set definition.
   *  The ribbon, the HR timeline, and the set detail sheet all read this so
   *  they can never disagree about what a set is. (Replaced the old
   *  stroke-continuity `sets`/`buildSets`, retired 2026-06-11.) */
  setGroups: SetGroup[];
  /** Auto-detected rest intervals based on lap gaps (HR-valley fallback). */
  restIntervals: RestInterval[];
  /** Workout phases — derived from rest gaps + position. Each phase is a block of
   *  continuous swimming separated from the next by a rest > 25s. Labels are
   *  heuristic ("Warm-up", "Main block", "Set 1 of N"). */
  phases: WorkoutPhase[];
  /** SWOLF efficiency aggregates. Null when no SWOLF data is present. */
  swolfStats: SwolfStats | null;
  /** 1-based lap indices that are >2SD outliers in EITHER direction (slow-side
   *  auto-excluded + fast-side kept-in) — the REVIEW-tier set the Plan 03 swipe
   *  deck surfaces as cards. Rest/wall laps are silent (D-02) and NOT included.
   *  Sourced from stampEffectiveExcluded's side-channel (07-01). */
  reviewLapIndices: number[];
  suspects: Suspect[];
  /** True for an open-water swim (GPS splits, no pool lengths). The views branch
   *  on this to say "splits" not "lengths" and to suppress pool-only chrome
   *  (SWOLF, length records). Pool swims are always false. Detected from
   *  `activities[].swimmingLocationType === "openWater"` (isOpenWaterFirehose). */
  isOpenWater: boolean;
  /** The view model for the corrections showcase (Tasks 3-7): which lengths he
   *  actually fixed, plus confirmed/unmatched disclosure counts. A fresh empty VM
   *  for open water — corrections are disabled there in v1 (see the isOpenWater
   *  branch above). */
  corrections: CorrectionsViewModel;
}

/**
 * @param labels persisted LapLabels (D-04) — REQUIRED, no default, so TypeScript
 *   forces BOTH call sites (Prototypes.svelte, SetTab.svelte) to be updated.
 *   This is the type-system version of D-03's "impossible to half-wire." For
 *   Plan 01, both call sites pass `[]`; Plan 03 wires the live persisted array.
 */
export function deriveWorkout(dump: SwimFirehoseResponse, labels: LapLabel[]): DerivedWorkout {
  const totalDurationSeconds = dump.workout.durationSeconds;
  const totalDistanceMeters = dump.workout.totalDistance?.value ?? 0;
  const hrBucket = dump.buckets.find((b) => b.typeName === "HKQuantityTypeIdentifierHeartRate") as FirehoseTypeBucket | undefined;
  const hrSamples = hrBucket?.samples ?? [];

  const isOpenWater = isOpenWaterFirehose(dump);

  // Open water has no pool lengths and no `lap` events — force poolLengthMeters
  // null (the guard that keeps it out of the pool-length record aggregates) and
  // build splits from the paired distance/stroke windows instead of lap events.
  const lapEvents = dump.events.filter((e) => e.type === "lap");
  const poolLengthMeters = isOpenWater ? null : detectPoolLength(lapEvents, totalDistanceMeters);

  let laps: Lap[];
  let reviewLapIndices: number[];
  let suspects: Suspect[];
  if (isOpenWater) {
    // Splits derive. Corrections + suspects are disabled for open water in v1:
    // the cohort logic is pool-length-shaped (fixed-distance same-stroke peers)
    // and the Watch's open-water stroke typing is unvalidated, so we never
    // exclude a split or surface a suspect. reviewLapIndices stays empty.
    laps = buildOpenWaterSplits(dump);
    reviewLapIndices = [];
    suspects = [];
  } else {
    laps = buildLaps(dump, poolLengthMeters);
    applyStrokeOverrides(laps, labels);   // the user's correction becomes the effective stroke before anything reads it
    ({ reviewLapIndices } = stampEffectiveExcluded(laps, labels));   // D-03: the single threading point, right after buildLaps
    const confirmed = confirmedLapIndices(laps, labels);
    suspects = findStrokeSuspects(laps).filter((s) => !confirmed.has(s.lapIndex));
  }

  const hrSeries = buildHrSeries(hrSamples);
  const aggregates = computeWorkoutAggregates(laps, poolLengthMeters, hrBucket);
  const setGroups = buildSetGroups(dump.events, laps, poolLengthMeters);
  const restIntervals = buildRestIntervals(laps, hrSeries, aggregates.avgHr);
  const phases = buildPhases(laps, poolLengthMeters);
  const swolfStats = computeSwolfStats(laps, phases);
  const corrections = isOpenWater
    ? emptyCorrections()
    : buildCorrectionsViewModel(laps, setGroups, labels);

  return {
    laps,
    poolLengthMeters,
    totalDistanceMeters,
    totalDurationSeconds,
    avgHr: aggregates.avgHr,
    maxHr: aggregates.maxHr,
    peakHrTs: aggregates.peakHrTs,
    peakLapIndex: aggregates.peakLapIndex,
    fastestLapIndex: aggregates.fastestLapIndex,
    strokeBreakdown: aggregates.strokeBreakdown,
    hrSeries,
    setGroups,
    restIntervals,
    phases,
    swolfStats,
    reviewLapIndices,
    suspects,
    isOpenWater,
    corrections,
  };
}
